<template>
  <div>
    <back-button default-to="/stock-ins"/>
    <b-card no-header>
      <div class="d-flex mb-3">
        <div>
          <h5 class="mb-1">{{ detail.transaction_no }}</h5>
          <div>{{ formatDate(detail.tanggal_masuk) }} · {{ sourceLabel(detail.sumber) }}</div>
        </div>
        <div class="ml-auto">
          <b-badge :variant="statusVariant(detail.approval_status)">{{ statusLabel(detail.approval_status) }}</b-badge>
        </div>
      </div>
      <b-alert :show="detail.approval_status === 'draft' && !!detail.returned_reason" variant="warning">
        Dikembalikan untuk diperbaiki: {{ detail.returned_reason }}
      </b-alert>
      <b-alert :show="detail.approval_status === 'rejected' && !!detail.rejected_reason" variant="danger">
        Ditolak oleh owner: {{ detail.rejected_reason }}
      </b-alert>
      <p v-if="detail.submitted_at" class="text-muted small">Diajukan oleh {{ actorName(detail.submitter) }} pada {{ formatDateTime(detail.submitted_at) }}.</p>
      <p v-if="detail.edited_at" class="text-muted small">Terakhir diubah oleh {{ actorName(detail.editor) }} pada {{ formatDateTime(detail.edited_at) }}.</p>
      <p v-if="detail.catatan"><strong>Catatan:</strong> {{ detail.catatan }}</p>
      <div class="table-responsive">
        <table class="table table-striped">
          <thead><tr><th>Nama</th><th>Jenis</th><th>Berat</th><th>Kadar</th><th>Jumlah</th><th>Harga Modal</th></tr></thead>
          <tbody>
            <tr v-for="item in detail.detail_barang" :key="item.id">
              <td>{{ item.nama }}<small v-if="item.stock_id" class="d-block text-muted">Stok #{{ item.stock_id }}</small></td>
              <td>{{ typeLabel(item.jenis_barang) }}</td>
              <td>{{ item.berat }} gram</td>
              <td>{{ item.kadar }}</td>
              <td>{{ item.qty }} buah</td>
              <td>Rp{{ $formatPrice(item.harga_modal) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="isAdmin && detail.approval_status === 'pending'" class="mt-3">
        <b-button variant="success" :disabled="isLoading" @click="approve">Setujui dan Tambahkan ke Stok</b-button>
        <b-button variant="warning" class="ml-2" :disabled="isLoading" @click="showReturn">Kembalikan ke Draft</b-button>
        <b-button variant="danger" class="ml-2" @click="showReject">Tolak</b-button>
      </div>
      <div v-if="canEditDraft" class="mt-3">
        <b-button variant="primary" :to="'/stock-ins/' + detail.id + '/edit'">Ubah Draft</b-button>
        <b-button variant="success" class="ml-2" :disabled="isLoading" @click="submit">Ajukan Persetujuan</b-button>
        <b-button variant="danger" class="ml-2" :disabled="isLoading" @click="removeDraft">Hapus Draft</b-button>
      </div>
      <div v-if="detail.approval_status === 'approved'" class="mt-3">
        <b-button v-if="!printRequest" variant="info" :disabled="isLoading" @click="requestBarcodePrint">Ajukan Cetak Barcode</b-button>
        <b-badge v-else-if="printRequest.status === 'on_review'" variant="warning">Pengajuan cetak barcode sedang direview</b-badge>
        <span v-else-if="printRequest.status === 'returned'"><b-badge variant="warning">Pengajuan dikembalikan</b-badge><small v-if="printRequest.review_note" class="text-muted ml-2">{{ printRequest.review_note }}</small><b-button variant="warning" class="ml-2" :disabled="isLoading" @click="requestBarcodePrint">Ajukan Ulang Cetak Barcode</b-button></span>
        <b-button v-else-if="printRequest.status === 'approved'" variant="primary" :to="'/print/barcodes?stock_in_id=' + detail.id + '&approval_request_id=' + printRequest.id"><font-awesome-icon icon="barcode"/> Cetak Barcode</b-button>
      </div>
      <b-button to="/stock-ins" variant="secondary" class="float-right">Kembali</b-button>
    </b-card>
    <b-modal ref="rejectModal" title="Tolak Stok Masuk" ok-title="Tolak" cancel-title="Batal" @ok="reject">
      <form-group-input type="textarea" label="Alasan Penolakan" :need-label="true" v-model="rejectReason" id="reject_reason" />
    </b-modal>
    <b-modal ref="returnModal" title="Kembalikan ke Draft" ok-title="Kembalikan" cancel-title="Batal" @ok="returnForRevision">
      <form-group-input type="textarea" label="Alasan Pengembalian" :need-label="true" v-model="returnReason" id="returned_reason" />
    </b-modal>
  </div>
</template>

<script>
import StockIn from '@/models/StockIn.js'
import formGroupInput from '@/components/FormGroupInput.vue'
import { Roles } from '@/_helpers'
import ApprovalRequest from '@/models/ApprovalRequest.js'

const stockInModel = new StockIn()
const approvalRequestModel = new ApprovalRequest()

export default {
  components: { formGroupInput },
  data () {
    return {
      detail: { detail_barang: [] },
      isLoading: false,
      rejectReason: '',
      returnReason: '',
      printRequest: null
    }
  },
  computed: {
    isAdmin () {
      return this.$store.getters['account/accountData'].role === Roles.ADMIN
    },
    isCreator () {
      return Number(this.detail.created_by) === Number(this.$store.getters['account/accountData'].id)
    },
    canEditDraft () {
      return this.detail.approval_status === 'draft' && this.isCreator
    },
  },
  mounted () {
    this.load()
  },
  methods: {
    async load () {
      this.detail = await stockInModel.find(this.$route.params.id)
      this.printRequest = this.detail.approval_status === 'approved'
        ? await approvalRequestModel.findPrintRequest('stock_in', this.$route.params.id)
        : null
    },
    statusLabel (status) {
      return { draft: 'Draft', pending: 'On Review', approved: 'Disetujui', rejected: 'Ditolak' }[status] || status
    },
    statusVariant (status) {
      return { draft: 'secondary', pending: 'warning', approved: 'success', rejected: 'danger' }[status] || 'secondary'
    },
    typeLabel (value) {
      return { emas: 'Emas', berlian: 'Berlian', lainnya: 'Lainnya', perhiasan: 'Lainnya' }[value] || value
    },
    sourceLabel (value) {
      return { supplier: 'Supplier', stok_awal: 'Stok Awal', produksi: 'Produksi', koreksi: 'Koreksi Stok', lainnya: 'Lainnya' }[value] || value
    },
    formatDate (value) {
      return value ? this.$moment(value).format('DD/MM/YYYY') : '-'
    },
    formatDateTime (value) {
      return value ? this.$moment(value).format('DD/MM/YYYY HH:mm') : '-'
    },
    actorName (actor) {
      return actor && actor.name ? actor.name : '-'
    },
    approve () {
      this.isLoading = true
      stockInModel.approve(this.$route.params.id).then(response => {
        this.detail = response
        this.$store.dispatch('notification/success', 'Stok masuk berhasil disetujui dan ditambahkan ke stok.')
      }).catch(error => this.$store.dispatch('notification/error', error)).finally(() => { this.isLoading = false })
    },
    submit () {
      this.isLoading = true
      stockInModel.submit(this.$route.params.id).then(response => {
        this.detail = response
        this.$store.dispatch('notification/success', 'Stok Masuk berhasil diajukan untuk persetujuan.')
      }).catch(error => this.$store.dispatch('notification/error', error)).finally(() => { this.isLoading = false })
    },
    removeDraft () {
      if (!window.confirm('Hapus draft Stok Masuk ini?')) return
      this.isLoading = true
      stockInModel.delete(this.$route.params.id).then(() => {
        this.$store.dispatch('notification/success', 'Draft Stok Masuk dihapus.')
        this.$router.push('/stock-ins')
      }).catch(error => this.$store.dispatch('notification/error', error)).finally(() => { this.isLoading = false })
    },
    requestBarcodePrint () {
      this.isLoading = true
      const action = this.printRequest && this.printRequest.status === 'returned'
        ? approvalRequestModel.resubmitPrint(this.printRequest.id)
        : approvalRequestModel.requestPrintBarcode('stock_in', this.$route.params.id)
      action.then(response => {
        this.printRequest = response
        this.$store.dispatch('notification/success', 'Pengajuan cetak barcode dikirim ke review.')
      }).catch(error => this.$store.dispatch('notification/error', error)).finally(() => { this.isLoading = false })
    },
    showReject () {
      this.$refs.rejectModal.show()
    },
    showReturn () {
      this.$refs.returnModal.show()
    },
    returnForRevision (event) {
      if (!this.returnReason) {
        event.preventDefault()
        this.$store.dispatch('notification/error', 'Alasan pengembalian wajib diisi.')
        return
      }
      this.isLoading = true
      stockInModel.returnForRevision(this.$route.params.id, this.returnReason).then(response => {
        this.detail = response
        this.returnReason = ''
        this.$store.dispatch('notification/success', 'Stok Masuk dikembalikan ke draft untuk diperbaiki.')
      }).catch(error => this.$store.dispatch('notification/error', error)).finally(() => { this.isLoading = false })
    },
    reject (event) {
      if (!this.rejectReason) {
        event.preventDefault()
        this.$store.dispatch('notification/error', 'Alasan penolakan wajib diisi')
        return
      }
      stockInModel.reject(this.$route.params.id, this.rejectReason).then(response => {
        this.detail = response
        this.$store.dispatch('notification/success', 'Pengajuan stock in ditolak.')
      }).catch(error => this.$store.dispatch('notification/error', error))
    }
  }
}
</script>
